/**
 * @file personal-coach.js — IMGA+ Personal Coach standalone purchase page
 *
 * Location: Shopify Theme > Assets > personal-coach.js
 * Page Template: src/templates/page.personal-coach.liquid
 * Page Handle:   /pages/personal-coach
 *
 * WHAT THIS IS
 * ────────────
 * The Phase 2 standalone purchase page for 1:1 Personal Coaching. It clones the
 * titan-web "Choose your coaching plan" modal (app/features/shopify) as a
 * Shopify page and adds the subscription product alongside the one-time
 * session-pack (standalone) products.
 *
 *   • Standalone  — Nutrition / Mental Performance, session packs (variants).
 *                   Shopper picks a discipline, then a session package, then
 *                   fills athlete info. One-time purchase (no selling plan).
 *   • Subscription — Monthly plan (4 sessions). No variant/session choice: the
 *                   only option IS the monthly plan. Adds a selling_plan_id so
 *                   the endpoint books it as a recurring subscription.
 *
 * PRODUCT DATA COMES FROM THE COACHING COLLECTION (server-rendered)
 * ─────────────────────────────────────────────────────────────────
 * page.personal-coach.liquid loops the coaching collection and writes each
 * variant's id (and, for the subscription, its selling_plan_id read from
 * variant.selling_plan_allocations) onto the DOM. Sourcing from Shopify — not
 * the imgpmapi catalog the modal reads — is deliberate: only Shopify exposes
 * the subscription's selling plan, and the collection stays in variant-id sync
 * automatically. This file holds no product source; it reads the DOM selection.
 *
 * HOW IT ADDS TO CART
 * ───────────────────
 * The page does NOT touch the cart directly. On Add to Cart it builds a
 * /pages/add-to-cart URL (Shopify custom endpoint — see
 * src/templates/page.add-to-cart.liquid + src/assets/add-to-cart.js and
 * docs/add-to-cart-endpoint.md) and navigates to it. That endpoint does the
 * /cart/add.js POST, attaches customer/participant data, and redirects to /cart.
 *
 *   items=[{ variant_id, quantity:1, selling_plan_id? }]   // plan only for sub
 *   + firstName/lastName/email/phoneNumber                 // athlete info
 *   + auth0_id / share_with / authorId                     // identity passthru
 *   + non_camp=true                                        // coaching ≠ camp
 *
 * IDENTITY (auth0_id / share_with)
 * ────────────────────────────────
 * Capture + cookie/cart persistence is owned sitewide by 01-coaching-identity.js
 * (loaded in layout/theme.liquid). This page does NOT re-implement capture — it
 * READS the identity (URL first, then the cookies that asset set:
 * _img_auth0_id / _img_share_with) and forwards it on the /pages/add-to-cart
 * URL, because a full-page navigation to the endpoint is not a /cart/add form
 * and so the sitewide form-injection path doesn't apply to it. authorId (rep
 * attribution) is forwarded the same way when present on the URL/cookie.
 *
 * The pure functions at the top are exported for unit tests
 * (tests/personal-coach.test.js) and hold the URL-building + identity rules.
 */

(function (root, factory) {
  'use strict';

  var api = factory();

  // Unit tests require this file directly; the browser gets the side effects.
  if (typeof module === 'object' && module.exports) {
    module.exports = api;
    return;
  }

  root.IMGPersonalCoach = api;
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', api.init);
  } else {
    api.init();
  }
})(typeof window !== 'undefined' ? window : this, function () {
  'use strict';

  // Subscription variant id + selling_plan_id are NOT hardcoded here — the
  // Liquid template resolves them natively from the coaching collection
  // (variant.selling_plan_allocations) and writes them onto the subscription
  // radio as data-pc-variant / data-pc-selling-plan. This file just reads
  // whatever the selected option carries.

  var CONFIG = {
    endpoint: '/pages/add-to-cart',
    // Identity cookies written by 01-coaching-identity.js (sitewide capture).
    cookieAuth0: '_img_auth0_id',
    cookieShareWith: '_img_share_with',
    // Rep attribution cookie written by 01-rep-functionality.js.
    cookieRep: '_img_rep',
    // Coaching is a non-camp product — tells the cart page to skip camp UI.
    nonCamp: true
  };

  // IMG Academy Lead API (Personal Coach Leads integration) — the
  // affiliateId is THIS integration's own id, not a per-shopper value; see
  // the Lead API dashboard's "How to share data" snippet for this form.
  var LEAD_API = {
    endpoint: 'https://3qodre34s1.execute-api.us-east-1.amazonaws.com/info/opt-in',
    affiliateId: '1f1c2700-417d-6c1e-b072-c5719e8f0d8e',
    timeoutMs: 10000
  };

  // BOTH stores' selling-plan config for the same 6 session tiers — prod
  // (imgacademy) and stage (imgor) have entirely separate catalogs/plan ids
  // for the same tiers, so which table applies is read at runtime
  // (sessionToSellingPlan) from data-pc-env on #personal-coach-app —
  // stamped by page.personal-coach.liquid from shop.permanent_domain, NOT
  // guessed here from window.location.hostname (a custom domain on either
  // store would make hostname-sniffing unreliable; see the same
  // shop.permanent_domain note in robots.txt.liquid/theme.liquid). Each
  // standalone session-pack size has its own subscription cadence — these
  // are the 6 SellingPlan ids' numeric leaf, keyed by session count, so the
  // right allocation gets picked off a subscription variant that may list
  // more than one (rather than trusting whichever happens to sort first).
  // 2 and 4 sessions are both billed on the SAME monthly (1-month) plan —
  // the plan governs billing frequency, not session count, so one plan
  // covers both tiers. Deliberately NOT derived from the storefront, since
  // nothing in the product/variant data itself says "this plan is the one
  // for the 12-session tier".
  var SESSION_TO_SELLING_PLAN_BY_ENV = {
    prod: { 2: 1760722990, 4: 1760722990, 6: 1833795630, 12: 1833893934, 18: 1833828398, 24: 1833861166 },
    stage: { 2: 6914179142, 4: 6914179142, 6: 6990954566, 12: 6990987334, 18: 6991020102, 24: 6991052870 }
  };

  /** Resolve the active environment's selling plan id for a session count. */
  function sessionToSellingPlan(root, sessionCount) {
    var env = (root.getAttribute('data-pc-env') === 'stage') ? 'stage' : 'prod';
    return SESSION_TO_SELLING_PLAN_BY_ENV[env][sessionCount];
  }

  // Cadence label for the Subscribe & Save eyebrow, keyed the same way as
  // SESSION_TO_SELLING_PLAN_BY_ENV's per-env tables — display-only, not sent
  // anywhere, and (unlike the plan ids) the same for both stores.
  var SESSION_TO_CADENCE_MONTHS = {
    2: 1,
    4: 1,
    6: 3,
    12: 6,
    18: 9,
    24: 12
  };

  /** "1 Month" (singular) vs "3 Months" — SESSION_TO_CADENCE_MONTHS values render as text in 4 places. */
  function pluralMonths(months) {
    return months + (months === 1 ? ' Month' : ' Months');
  }

  // Two top-level tabs replace the old separate "how would you like to pay"
  // step — each tab has its OWN fixed set of session tiers, never mixed:
  //   Subscription — 2/4 sessions only, relabeled Off-season/In-season.
  //   Pay as you go — 6/12/18/24 sessions, the original tiers.
  var SUBSCRIPTION_TAB_TIERS = [2, 4];
  var PAYG_TAB_TIERS = [6, 12, 18, 24];
  var SESSION_LABEL_OVERRIDE = { 2: 'Off-season', 4: 'In-season' };
  /** Corner callout badge — purely cosmetic, same pill styling as the Pay yearly savings badge. */
  var SESSION_BADGE = { 18: 'Most popular', 24: 'Best value' };
  /** Hidden for now — flip to true to bring the corner badges back. */
  var SHOW_SESSION_BADGES = false;

  // Shared by the input-wiring loop and updateFilledState — every text
  // field in the Athlete Info + Guardian Info fieldset.
  var ATHLETE_FIELD_SELECTOR =
    '[name="firstName"],[name="lastName"],[name="email"],[name="phoneNumber"],' +
    '[name="guardianFirstName"],[name="guardianLastName"],[name="guardianEmail"],[name="guardianPhone"]';

  // ──────────────────────────────────────────────────────────────────────────
  // PURE HELPERS — unit tested (tests/personal-coach.test.js)
  // ──────────────────────────────────────────────────────────────────────────

  /**
   * Read a single decoded query param from a raw search string. Mirrors the
   * getParam() in 01-coaching-identity.js (src/assets/* is served verbatim, no
   * polyfills), so behaviour matches the sitewide identity capture exactly.
   *
   * @param {string} search - e.g. "?auth0_id=auth0%7Cabc&share_with=x,y"
   * @param {string} name
   * @returns {string} decoded value, or "" when absent/blank/undecodable
   */
  function getParam(search, name) {
    if (typeof search !== 'string' || !search || typeof name !== 'string') {
      return '';
    }
    var query = search.charAt(0) === '?' ? search.slice(1) : search;
    var pairs = query.split('&');
    for (var i = 0; i < pairs.length; i++) {
      var eq = pairs[i].indexOf('=');
      var key = eq === -1 ? pairs[i] : pairs[i].slice(0, eq);
      if (key !== name) continue;
      var raw = eq === -1 ? '' : pairs[i].slice(eq + 1);
      try {
        return decodeURIComponent(raw.replace(/\+/g, ' ')).trim();
      } catch (e) {
        return '';
      }
    }
    return '';
  }

  /** Read a cookie value by name, or "" when absent. */
  function getCookie(cookieString, name) {
    if (typeof cookieString !== 'string' || !cookieString) return '';
    var parts = cookieString.split(';');
    for (var i = 0; i < parts.length; i++) {
      var part = parts[i].trim();
      var eq = part.indexOf('=');
      if (eq === -1) continue;
      if (part.slice(0, eq) === name) {
        try {
          return decodeURIComponent(part.slice(eq + 1)).trim();
        } catch (e) {
          return '';
        }
      }
    }
    return '';
  }

  // Values downstream JSON.parse and choke on — never forward them.
  function isUsableValue(value) {
    return (
      typeof value === 'string' &&
      value !== '' &&
      value !== 'null' &&
      value !== 'undefined'
    );
  }

  /**
   * Normalize share_with to the JSON-array-string shape the endpoint contract
   * requires. Per docs/add-to-cart-endpoint.md, the endpoint writes share_with
   * through UNVALIDATED, so the caller must send a valid JSON array string
   * ('["a@x.com"]') — client-side validation is the caller's job. The IMG app
   * may hand us a bare token ('abc') or a ready array ('["abc"]'); this makes
   * both land as a valid array. Returns '' for empty/unusable input (caller
   * then omits the param).
   *
   * @param {string} value - raw share_with (URL param or cookie)
   * @returns {string} JSON-array string, or '' when there's nothing to send
   */
  function normalizeShareWith(value) {
    if (!isUsableValue(value)) return '';
    var trimmed = value.trim();
    // Already a JSON array? Keep it verbatim only if it parses to a non-empty
    // array of usable entries; a malformed '[...]' is treated as a bare token.
    if (trimmed.charAt(0) === '[') {
      try {
        var parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) {
          var cleaned = parsed.filter(function (entry) {
            return isUsableValue(typeof entry === 'string' ? entry : String(entry));
          });
          return cleaned.length ? JSON.stringify(cleaned) : '';
        }
      } catch (e) {
        // fall through — wrap the raw string as a single-entry array
      }
    }
    return JSON.stringify([trimmed]);
  }

  /**
   * Resolve identity for the endpoint URL: URL param wins, cookie is the
   * fallback (a shopper who navigated away from the entry URL still carries the
   * cookie the sitewide asset set on arrival).
   *
   * @param {string} search       - window.location.search
   * @param {string} cookieString - document.cookie
   * @returns {{auth0Id:string, shareWith:string, rep:string}}
   */
  function resolveIdentity(search, cookieString) {
    var auth0Id = getParam(search, 'auth0_id') || getCookie(cookieString, CONFIG.cookieAuth0);
    var shareWith = getParam(search, 'share_with') || getCookie(cookieString, CONFIG.cookieShareWith);
    // authorId / author_id are both accepted by the endpoint; read either.
    var rep =
      getParam(search, 'authorId') ||
      getParam(search, 'author_id') ||
      getCookie(cookieString, CONFIG.cookieRep);

    return {
      auth0Id: isUsableValue(auth0Id) ? auth0Id : '',
      // Endpoint contract: share_with must be a JSON-array string, written
      // through unvalidated — normalize here (docs/add-to-cart-endpoint.md).
      shareWith: normalizeShareWith(shareWith),
      rep: isUsableValue(rep) ? rep : ''
    };
  }

  /**
   * Build the /pages/add-to-cart URL for a single coaching line item.
   *
   * @param {Object} args
   * @param {number|string} args.variantId          - required Shopify variant id
   * @param {number|string} [args.sellingPlanId]    - subscription plan; omit for one-time
   * @param {Object} args.athlete                   - {firstName,lastName,email,phone}
   * @param {Object} [args.identity]                - {auth0Id,shareWith,rep}
   * @param {string} [args.endpoint]                - defaults CONFIG.endpoint
   * @returns {string} the endpoint URL to navigate to
   */
  function buildAddToCartUrl(args) {
    args = args || {};
    if (!args.variantId) {
      throw new Error('buildAddToCartUrl: variantId is required');
    }

    var item = { variant_id: args.variantId, quantity: 1 };
    if (args.sellingPlanId) {
      // Per-item, numeric leaf SellingPlan id — endpoint maps this to Shopify
      // selling_plan and books the item as a recurring subscription.
      item.selling_plan_id = args.sellingPlanId;
    }

    var params = new URLSearchParams();
    params.set('items', JSON.stringify([item]));

    var a = args.athlete || {};
    if (a.firstName) params.set('firstName', a.firstName);
    if (a.lastName) params.set('lastName', a.lastName);
    if (a.email) params.set('email', a.email);
    if (a.phone) params.set('phoneNumber', a.phone);
    // Guardian fields ride the SAME param names add-to-cart.js already reads
    // into attributes.customer.parent (buildCustomerProperties) — "parent"
    // there, not "guardian"; this page's own naming is just the UI label.
    if (a.guardianFirstName) params.set('parentFirstName', a.guardianFirstName);
    if (a.guardianLastName) params.set('parentLastName', a.guardianLastName);
    if (a.guardianEmail) params.set('parentEmail', a.guardianEmail);
    if (a.guardianPhone) params.set('parentPhoneNumber', a.guardianPhone);

    var id = args.identity || {};
    if (isUsableValue(id.auth0Id)) params.set('auth0_id', id.auth0Id);
    // share_with only rides alongside a primary user — a share for nobody is a
    // puzzle downstream (mirrors 01-coaching-identity.js buildLineItemProperties).
    if (isUsableValue(id.auth0Id) && isUsableValue(id.shareWith)) {
      params.set('share_with', id.shareWith);
    }
    if (isUsableValue(id.rep)) params.set('authorId', id.rep);

    if (CONFIG.nonCamp) params.set('non_camp', 'true');

    // Isolated app-purchase funnel: skip /cart, go straight to checkout. The
    // endpoint reads redirect=checkout and lands the shopper on /checkout.
    params.set('redirect', 'checkout');

    var endpoint = args.endpoint || CONFIG.endpoint;
    return endpoint + '?' + params.toString();
  }

  /**
   * Submit this purchase as a lead to the IMG Academy Lead API. NEVER
   * rejects — a network error, a non-2xx response, and the LEAD_API.timeoutMs
   * cap itself (via AbortController) are all swallowed and just resolve, so
   * callers can safely treat "the promise settled" as "stop waiting, proceed
   * regardless." Per spec: a lead failure must never block or delay the
   * actual sale past that cap. Resolves immediately on success/failure
   * rather than always waiting the full timeout.
   *
   * @param {{firstName,lastName,email,phone,guardianFirstName,guardianLastName,guardianEmail,guardianPhone}} athlete
   * @param {string} [disciplineTitle] - the product title of the selected discipline card
   * @returns {Promise<void>}
   */
  function sendLeadToApi(athlete, disciplineTitle) {
    return new Promise(function (resolve) {
      if (typeof fetch !== 'function' || typeof Promise !== 'function') {
        resolve();
        return;
      }

      var payload = {
        affiliateId: LEAD_API.affiliateId,
        FirstName: athlete.firstName,
        LastName: athlete.lastName,
        Email: athlete.email,
        // This integration is Personal Coaching only — always IMGA+.
        img_marketobusinesstype: 'IMGA+',
        marketingOptIn: true,
        dataSharingOptIn: true
      };
      // Optional fields per the Lead API's own schema — only sent when present.
      if (athlete.phone) payload.MobilePhone = athlete.phone;
      if (athlete.guardianFirstName) payload.parentFirstName = athlete.guardianFirstName;
      if (athlete.guardianLastName) payload.parentLastName = athlete.guardianLastName;
      if (athlete.guardianEmail) payload.parentEmail = athlete.guardianEmail;
      if (athlete.guardianPhone) payload.parentPhone = athlete.guardianPhone;
      var division = leadMarketoDivision(disciplineTitle);
      if (division) payload.img_marketodivision = division;

      var settled = false;
      function finish() {
        if (settled) return;
        settled = true;
        resolve();
      }

      var controller = (typeof AbortController === 'function') ? new AbortController() : null;
      var timeoutId = setTimeout(function () {
        if (controller) controller.abort();
        finish();
      }, LEAD_API.timeoutMs);

      fetch(LEAD_API.endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        signal: controller ? controller.signal : undefined
      })
        .catch(function (err) {
          console.warn('personal-coach.js: lead API submission failed (ignored — not blocking the sale)', err);
        })
        .then(function () {
          clearTimeout(timeoutId);
          finish();
        });
    });
  }

  // ──────────────────────────────────────────────────────────────────────────
  // DOM WIRING (browser only)
  //
  // Product data (standalone variants + the subscription variant/selling_plan)
  // is rendered server-side by page.personal-coach.liquid from the coaching
  // collection, so this file holds NO product source — it reads the selected
  // variant + selling plan off the DOM and builds the endpoint URL.
  // ──────────────────────────────────────────────────────────────────────────

  function init() {
    if (typeof document === 'undefined') return;
    var root = document.getElementById('personal-coach-app');
    if (!root) return;

    var form = root.querySelector('[data-pc-form]');
    if (!form) return;

    // Discipline change rebuilds BOTH tab panels' session cards and refreshes
    // the footer. The session pick is stale once it changes (a new
    // discipline's tiers/subscription variants are different), so re-picking
    // a discipline clears it. Athlete info is left as-is (retained, not
    // cleared) — applyGates() re-disables the fieldset until a variant
    // resolves again, same as a first-time visit; the shopper doesn't have
    // to retype it.
    wireDisciplineChange(root, function () {
      resetSessionSelection(root);
      refresh(root);
      updateYearlySavingsPill(root);
      // Re-measure: the pill's width just changed ("Save" -> "Save 50%"),
      // which shifts the Pay yearly option's own width.
      positionCadenceHighlight(root);
      // Auto-scroll disabled for now (scrollToGate still defined below).
    });
    wireDisciplineDescToggles(root);

    // Top-level Subscription / Pay as you go tabs — each has its own fixed
    // session tiers (never mixed), so switching tabs resets whatever session
    // was picked under the other one, same spirit as a discipline change
    // resetting everything downstream.
    wirePurchaseTabs(root, function () {
      refresh(root);
    });

    // Pay monthly (subscription variant + plan) / Pay yearly (the standalone
    // variant, one-time — no plan at all) — only affects the Subscription
    // tab's own 2/4-session cards; rebuilds them in place, preserving
    // whichever tier was already selected (if any).
    wireSubCadence(root, function () {
      refresh(root);
    });

    // "Save 10% extra when you subscribe to this pack" — only affects the
    // Pay as you go tab's own 6/12/18/24-session cards; same in-place rebuild.
    wirePaygToggle(root, function () {
      refresh(root);
    });

    // Reflow the cadence highlight's measured left/width on viewport changes
    // (the toggle's own width, and so the pill's position, can shift at
    // narrower breakpoints) — only matters while the Subscription tab/panel
    // is actually visible; positionCadenceHighlight no-ops otherwise.
    window.addEventListener('resize', function () {
      if (getActivePurchaseTab(root) === 'subscription') positionCadenceHighlight(root);
    });

    // Session cards (either tab): delegated on each panel's container rather
    // than attached per-radio — renderSessionOptions fully rebuilds those
    // radios on every discipline/cadence/toggle change, and a container
    // listener survives that innerHTML replacement without re-wiring.
    var sessionContainers = root.querySelectorAll('[data-pc-session-options]');
    for (var sc = 0; sc < sessionContainers.length; sc++) {
      sessionContainers[sc].addEventListener('change', function (e) {
        if (!e.target.matches('[data-pc-session-option]')) return;
        refresh(root);
        // Auto-scroll disabled for now (scrollToGate still defined below).
      });
    }
    var fields = root.querySelectorAll(ATHLETE_FIELD_SELECTOR);
    for (var f = 0; f < fields.length; f++) {
      (function (field) {
        field.addEventListener('input', function () {
          sanitizeAthleteField(field);
          validateAthleteField(field);
          refresh(root);
        });
      })(fields[f]);
    }

    // Prime the initial summary/submit state + plan-card prices.
    refresh(root);
    // Subscription is the default-visible tab/panel (Liquid default) — its
    // cadence highlight needs an initial position too, not just on tab click.
    if (getActivePurchaseTab(root) === 'subscription') positionCadenceHighlight(root);

    form.addEventListener('submit', function (event) {
      event.preventDefault();

      var selection = resolveSelection(root);
      if (!selection.variantId || !canSubmit(root)) return;

      var athlete = readAthlete(root);
      var url = buildAddToCartUrl({
        variantId: selection.variantId,
        sellingPlanId: selection.sellingPlanId,
        athlete: athlete,
        identity: resolveIdentity(window.location.search, document.cookie)
      });

      // Give the lead API up to LEAD_API.timeoutMs to respond — sendLeadToApi
      // never rejects (a network error, non-2xx, or that timeout itself all
      // just resolve), so checkout always proceeds right after, success or
      // not. A full-page navigation would otherwise cancel the fetch before
      // it even got its 10 seconds, so this waits rather than firing the
      // request and navigating immediately.
      var submitBtn = root.querySelector('[data-pc-submit]');
      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.textContent = 'Adding to Cart...';
      }
      sendLeadToApi(athlete, selection.disciplineTitle).then(function () {
        window.location.assign(url);
      });
    });
  }

  /**
   * The currently-checked discipline card. Holds BOTH the standalone variants
   * (data-pc-variants) AND the matching subscription variant + selling plan
   * (data-pc-sub-*), so subscription applies to the selected discipline.
   */
  function selectedDiscipline(root) {
    return root.querySelector('[data-pc-discipline]:checked');
  }

  /** The currently-checked session-package card (data-pc-session-option radio). */
  function selectedSession(root) {
    return root.querySelector('[data-pc-session-option]:checked');
  }

  /**
   * Resolve the chosen variant + selling plan + price (cents) off the DOM.
   * Price/variant/selling-plan are now baked onto the session card itself at
   * BUILD time (buildSessionOptionCard, per the active cadence/toggle for
   * whichever tab it belongs to) — this just reads them straight off the
   * checked radio's own data attributes. mode is derived from whether
   * data-pc-selling-plan is present, not re-looked-up here.
   */
  function resolveSelection(root) {
    var disc = selectedDiscipline(root);
    var disciplineTitle = disc ? disc.getAttribute('data-pc-title') || '' : '';

    var opt = selectedSession(root);
    if (!opt) {
      return { mode: '', variantId: '', sellingPlanId: '', priceCents: '', packageLabel: '', disciplineTitle: disciplineTitle };
    }

    var sellingPlanId = opt.getAttribute('data-pc-selling-plan') || '';
    return {
      mode: sellingPlanId ? 'subscription' : 'standalone',
      variantId: opt.value,
      sellingPlanId: sellingPlanId,
      priceCents: opt.getAttribute('data-pc-price') || '',
      packageLabel: opt.getAttribute('data-pc-label') || '',
      disciplineTitle: disciplineTitle
    };
  }

  /** Parse the discipline card's data-pc-sub-variants JSON (see the Liquid template). */
  function parseSubVariants(raw) {
    if (!raw) return [];
    try {
      var parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }

  /** True for the subscription product's dedicated annual-billing variants ("2 Sessions Billed Yearly") — never shown verbatim to the shopper, only used to resolve price/plan. */
  function isBilledYearlyLabel(label) {
    return /billed\s*yearly/i.test(label || '');
  }

  /**
   * Find the selected discipline's subscription variant for a given session
   * count, and the specific selling_plan_allocation matching
   * sessionToSellingPlan(root, sessionCount) (the active environment's
   * table) — not just that variant's first allocation, since a variant can
   * legitimately list more than one plan. Explicitly skips the "Billed
   * Yearly" variants (same session count, different cadence/plan) — those
   * are findYearlyVariantForTier's job, not this one's.
   * Falls back to the first allocation (with a console.warn) if the expected
   * plan id isn't present, so a catalog/config mismatch is loud rather than a
   * silently wrong price — this still beats refusing to sell outright.
   */
  function findSubscriptionForTier(root, sessionCount) {
    if (!sessionCount) return null;
    var disc = selectedDiscipline(root);
    if (!disc) return null;
    var subVariants = parseSubVariants(disc.getAttribute('data-pc-sub-variants'));

    var variant = null;
    for (var i = 0; i < subVariants.length; i++) {
      if (sessionCountFromLabel(subVariants[i].label) === sessionCount && !isBilledYearlyLabel(subVariants[i].label)) {
        variant = subVariants[i];
        break;
      }
    }
    if (!variant) return null;

    var plans = variant.plans || [];
    var wantPlanId = sessionToSellingPlan(root, sessionCount);
    var chosen = null;
    for (var p = 0; p < plans.length; p++) {
      if (String(plans[p].planId) === String(wantPlanId)) {
        chosen = plans[p];
        break;
      }
    }
    if (!chosen && plans.length) {
      console.warn(
        'personal-coach.js: no selling plan ' + wantPlanId + ' on the ' +
        sessionCount + '-session subscription variant — using its first plan instead.',
        variant
      );
      chosen = plans[0];
    }
    if (!chosen) return null;

    return {
      variantId: variant.id,
      sellingPlanId: chosen.planId,
      priceCents: chosen.price,
      packageLabel: cleanSessionLabel(variant.label)
    };
  }

  /**
   * "Pay yearly" (Subscription tab, 2/4-session tiers only): a DEDICATED
   * subscription-product variant ("2 Sessions Billed Yearly" / "4 Sessions
   * Billed Yearly"), distinct from the plain monthly-cadence variant of the
   * same session count, carrying the 24-session tier's annual selling plan
   * (sessionToSellingPlan(root, 24) — same plan id, every tier that bills
   * yearly shares it). The "Billed Yearly" wording is catalog/admin-facing
   * only — never surfaced to the shopper (title/subhead always come from the
   * plain variant passed into buildSessionOptionCard, never this one).
   * Falls back to null (caller then falls back further to the plain
   * one-time price) if the variant/plan isn't there yet.
   */
  function findYearlyVariantForTier(root, sessionCount) {
    if (!sessionCount) return null;
    var disc = selectedDiscipline(root);
    if (!disc) return null;
    var subVariants = parseSubVariants(disc.getAttribute('data-pc-sub-variants'));

    var variant = null;
    for (var i = 0; i < subVariants.length; i++) {
      if (sessionCountFromLabel(subVariants[i].label) === sessionCount && isBilledYearlyLabel(subVariants[i].label)) {
        variant = subVariants[i];
        break;
      }
    }
    if (!variant) return null;

    var plans = variant.plans || [];
    var wantPlanId = sessionToSellingPlan(root, 24);
    var chosen = null;
    for (var p = 0; p < plans.length; p++) {
      if (String(plans[p].planId) === String(wantPlanId)) {
        chosen = plans[p];
        break;
      }
    }
    if (!chosen && plans.length) {
      console.warn(
        'personal-coach.js: no selling plan ' + wantPlanId + ' on the ' +
        sessionCount + '-session Billed Yearly variant — using its first plan instead.',
        variant
      );
      chosen = plans[0];
    }
    if (!chosen) return null;

    return { variantId: variant.id, sellingPlanId: chosen.planId, priceCents: chosen.price };
  }

  /**
   * "Save X%" on the Pay yearly toggle — computed from the catalog's own
   * monthly vs. yearly prices (2-session tier, the toggle's canonical
   * reference — both tiers are expected to carry the same discount ratio by
   * design), NOT a hardcoded number. Percent = how much cheaper yearly's
   * monthly-equivalent rate (annual ÷ 12) is than the real monthly rate.
   */
  function computeYearlySavingsPercent(root) {
    var monthly = findSubscriptionForTier(root, 2);
    var yearly = findYearlyVariantForTier(root, 2);
    if (!monthly || !yearly || !monthly.priceCents) return null;
    var yearlyPerMonth = yearly.priceCents / 12;
    var percent = Math.round((1 - yearlyPerMonth / monthly.priceCents) * 100);
    return (percent > 0) ? percent : null;
  }

  /** Fills in the Pay yearly toggle's "Save" badge with the real computed percent, once a discipline's prices are available. */
  function updateYearlySavingsPill(root) {
    var pill = root.querySelector('[data-pc-yearly-savings-pill]');
    if (!pill) return;
    var percent = computeYearlySavingsPercent(root);
    pill.textContent = percent ? ('Save ' + percent + '%') : 'Save';
  }

  /** Which top-level tab is active — defaults to 'subscription' (matches the Liquid default). */
  function getActivePurchaseTab(root) {
    return root.getAttribute('data-pc-purchase-tab') === 'payg' ? 'payg' : 'subscription';
  }

  /**
   * Switch the active tab: swap which panel is visible/hidden and which tab
   * button looks active, stamp the new tab on the root (CSS hook + read back
   * by getActivePurchaseTab), and reset whatever session was picked under
   * the OTHER tab — the two tabs have entirely disjoint tier sets, so a
   * stale cross-tab selection would resolve to nothing meaningful anyway.
   */
  function setActivePurchaseTab(root, tab) {
    root.setAttribute('data-pc-purchase-tab', tab);

    var buttons = root.querySelectorAll('[data-pc-purchase-tab]');
    for (var i = 0; i < buttons.length; i++) {
      var isActive = buttons[i].getAttribute('data-pc-purchase-tab') === tab;
      buttons[i].classList.toggle('personal-coach__tab--active', isActive);
      buttons[i].setAttribute('aria-selected', isActive ? 'true' : 'false');
    }

    var panels = root.querySelectorAll('[data-pc-purchase-panel]');
    for (var p = 0; p < panels.length; p++) {
      panels[p].hidden = panels[p].getAttribute('data-pc-purchase-panel') !== tab;
    }

    resetSessionSelection(root);
    resetPaygToggle(root);
    if (tab === 'subscription') positionCadenceHighlight(root);
  }

  function wirePurchaseTabs(root, onChange) {
    var buttons = root.querySelectorAll('[data-pc-purchase-tab]');
    for (var i = 0; i < buttons.length; i++) {
      buttons[i].addEventListener('click', function (e) {
        var tab = e.currentTarget.getAttribute('data-pc-purchase-tab');
        if (tab === getActivePurchaseTab(root)) return;
        setActivePurchaseTab(root, tab);
        if (onChange) onChange();
      });
    }
  }

  /**
   * Slide the Pay monthly/yearly highlight pill under whichever option is
   * checked. Measured, not CSS-only, because the two options are different
   * widths ("Pay yearly" carries the Save 50% badge) — offsets only work
   * once the toggle is actually visible (non-zero offsetWidth), so callers
   * must invoke this AFTER unhiding the Subscription panel, not before.
   */
  function positionCadenceHighlight(root) {
    var toggle = root.querySelector('[data-pc-cadence-toggle]');
    var highlight = root.querySelector('[data-pc-cadence-highlight]');
    var checked = root.querySelector('[data-pc-sub-cadence]:checked');
    if (!toggle || !highlight || !checked) return;
    var option = checked.closest('.personal-coach__cadence-option');
    if (!option || option.offsetWidth === 0) return;
    highlight.style.left = option.offsetLeft + 'px';
    highlight.style.width = option.offsetWidth + 'px';
  }

  /** Uncheck every session card in BOTH tab panels. */
  function resetSessionSelection(root) {
    var radios = root.querySelectorAll('[data-pc-session-option]');
    for (var i = 0; i < radios.length; i++) radios[i].checked = false;
  }

  /**
   * Uncheck "Save 10% extra", rebuilding the Pay as you go cards back to
   * standalone pricing if it had been checked — any upstream change
   * (discipline, tab switch) should not let a stale subscribe-to-this-pack
   * choice survive it. No-ops (no rebuild) when already unchecked.
   */
  function resetPaygToggle(root) {
    var toggle = root.querySelector('[data-pc-payg-subscribe-toggle]');
    if (!toggle || !toggle.checked) return;
    toggle.checked = false;
    renderSessionOptionsForPanel(root, 'payg', PAYG_TAB_TIERS, paygToggleMode(root));
  }

  /** Subscription tab's own mode: Pay yearly = standalone (one-time), Pay monthly = subscription. */
  function subCadenceMode(root) {
    var cadence = root.querySelector('[data-pc-sub-cadence]:checked');
    return (cadence && cadence.value === 'yearly') ? 'standalone' : 'subscription';
  }

  /** Pay as you go tab's own mode: unchecked = standalone (one-time), checked = subscription (Save 10%). */
  function paygToggleMode(root) {
    var toggle = root.querySelector('[data-pc-payg-subscribe-toggle]');
    return (toggle && toggle.checked) ? 'subscription' : 'standalone';
  }

  /**
   * Pay monthly/yearly only ever affects the Subscription tab's own 2/4
   * cards — rebuild just that panel in place (renderSessionOptionsForPanel
   * preserves whichever tier was already checked, if any).
   */
  function wireSubCadence(root, onChange) {
    var radios = root.querySelectorAll('[data-pc-sub-cadence]');
    for (var i = 0; i < radios.length; i++) {
      radios[i].addEventListener('change', function () {
        positionCadenceHighlight(root);
        renderSessionOptionsForPanel(root, 'subscription', SUBSCRIPTION_TAB_TIERS, subCadenceMode(root));
        if (onChange) onChange();
      });
    }
  }

  /** Same idea as wireSubCadence, for the Pay as you go tab's own toggle. */
  function wirePaygToggle(root, onChange) {
    var toggle = root.querySelector('[data-pc-payg-subscribe-toggle]');
    if (!toggle) return;
    toggle.addEventListener('change', function () {
      renderSessionOptionsForPanel(root, 'payg', PAYG_TAB_TIERS, paygToggleMode(root));
      if (onChange) onChange();
    });
  }

  /** Mirrors the modal's canSubmit: variant + first/last + valid email. */
  function canSubmit(root) {
    var selection = resolveSelection(root);
    if (!selection.variantId) return false;
    var a = readAthlete(root);
    // Name length is capped natively (maxlength=50 in the markup) rather than
    // validated here — nothing to flag until they leave it empty and try to
    // submit, and the submit button is already disabled for that case.
    // Guardian fields are required too — unlike the athlete's own phone,
    // guardianPhone must be present AND pass the digits-only format check.
    return Boolean(
      a.firstName && a.lastName && isValidEmail(a.email) && isValidPhone(a.phone) &&
      a.guardianFirstName && a.guardianLastName && isValidEmail(a.guardianEmail) &&
      a.guardianPhone && isValidPhone(a.guardianPhone)
    );
  }

  function isValidEmail(value) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim());
  }

  // Phone is optional — only validate the digits-only rule once something is
  // typed; an empty field isn't itself an error (no phone required).
  function isValidPhone(value) {
    var v = String(value || '').trim();
    return v === '' || /^[0-9]+$/.test(v);
  }

  // Letters (incl. accented Latin — José, Müller, François), space, hyphen,
  // apostrophe, period — the standard set for real names (compound names,
  // O'Brien-style, "Jr."/"St." abbreviations, diacritics). Anything else
  // (digits, symbols) is stripped as it's typed, not just flagged.
  //
  // Email is NOT filtered the same way: RFC 5321's local-part legitimately
  // allows symbols this simple approach would have to either strip (risking
  // silently corrupting a real address, e.g. "o'brien@x.com") or special-case
  // one by one, and it still wouldn't cover valid international (RFC 6531)
  // addresses. Format is already checked properly by isValidEmail() + the
  // inline error below — that's the right layer for email, not character
  // filtering.
  var NAME_DISALLOWED = /[^a-zA-ZÀ-ɏḀ-ỿ' \-.]/g;

  /** Strip characters that aren't allowed in a name as the user types. */
  function sanitizeAthleteField(field) {
    var isNameField = field.name === 'firstName' || field.name === 'lastName' ||
      field.name === 'guardianFirstName' || field.name === 'guardianLastName';
    if (!isNameField) return;
    var cleaned = field.value.replace(NAME_DISALLOWED, '');
    if (cleaned !== field.value) field.value = cleaned;
  }

  /**
   * Show/hide the inline error under one athlete/guardian field (data-pc-error
   * sibling span). Only email/phone have a live format rule; name fields have
   * no inline error — length is capped natively and emptiness is caught by
   * the submit button staying disabled, not a per-keystroke message. Guardian
   * phone is REQUIRED (unlike the athlete's own optional phoneNumber), but
   * that's a canSubmit/required-attribute concern, not a format one — same
   * "no inline error for emptiness" rule still applies here.
   */
  function validateAthleteField(field) {
    var value = field.value;
    var message = '';
    if ((field.name === 'email' || field.name === 'guardianEmail') && value && !isValidEmail(value)) {
      message = 'Enter a valid email address.';
    } else if ((field.name === 'phoneNumber' || field.name === 'guardianPhone') && value && !isValidPhone(value)) {
      message = 'Phone number can only contain numbers.';
    }

    field.classList.toggle('personal-coach__input--error', !!message);
    var errorEl = field.parentElement && field.parentElement.querySelector('[data-pc-error]');
    if (errorEl) {
      errorEl.textContent = message;
      errorEl.hidden = !message;
    }
  }

  /**
   * Live-update the plan-card prices + dark footer summary + submit state.
   * The plan cards show prices for the CHOSEN SESSION TIER specifically (both
   * modes), not a discipline-wide "starting at" — re-filled on every session
   * change, not just once per discipline.
   */
  function refresh(root) {
    var selection = resolveSelection(root);

    var opt = selectedSession(root);
    // Baked onto the radio at build time (buildSessionOptionCard) — reflects
    // the ACTUAL chosen cadence for the Subscription tab's 2/4-session tiers
    // (1 month Pay monthly, 12 Pay yearly), not just the tier's fixed table
    // value, which both cadences would otherwise share.
    var months = opt ? (parseInt(opt.getAttribute('data-pc-valid-months'), 10) || null) : null;

    // Session-package disclaimers — valid-through date always (tied to the
    // session choice itself). Roll-over wording only makes sense for a
    // RECURRING cadence (sessions reset each billing cycle) — a one-time
    // purchase has no cycle to roll over from, so hide that line for it.
    var disclaimers = root.querySelector('[data-pc-session-disclaimers]');
    if (disclaimers) disclaimers.hidden = !months;
    var rollover = root.querySelector('[data-pc-rollover-disclaimer]');
    if (rollover) rollover.hidden = selection.mode === 'standalone';
    setText(root, '[data-pc-session-valid-through]', months ? ('Sessions valid through ' + sessionValidThroughDate(months)) : '');

    setText(root, '[data-pc-summary-discipline]', shortDisciplineLabel(selection.disciplineTitle) || '—');
    setText(root, '[data-pc-summary-package]', selection.packageLabel || '—');
    setText(root, '[data-pc-summary-cadence]', selection.mode === 'subscription' ? 'Subscription' : (selection.mode === 'standalone' ? 'One time purchase' : '—'));
    setText(root, '[data-pc-summary-price]', selection.priceCents ? formatPrice(selection.priceCents) : '—');
    // Mirrors the chosen card's OWN billed-cadence text verbatim (data-pc-billed-text,
    // baked on at build time) — same wording as the step the shopper just saw,
    // not a separately-worded restatement that could drift from it.
    setText(root, '[data-pc-summary-price-note]', opt ? (opt.getAttribute('data-pc-billed-text') || '') : '');

    var submit = root.querySelector('[data-pc-submit]');
    if (submit) submit.disabled = !canSubmit(root);

    updateFilledState(root);
    applyGates(root);
  }

  /**
   * Mark each athlete input as filled via data-pc-filled (CSS hook for the
   * "completed" blue border + grey fill). NOT done with :placeholder-shown —
   * that pseudo-class doesn't reliably compute on a disabled input across
   * browsers, which was making disabled-and-actually-empty fields wrongly
   * read as filled. Reading .value directly works regardless of disabled.
   */
  function updateFilledState(root) {
    var fields = root.querySelectorAll(ATHLETE_FIELD_SELECTOR);
    for (var i = 0; i < fields.length; i++) {
      if (fields[i].value) fields[i].setAttribute('data-pc-filled', '');
      else fields[i].removeAttribute('data-pc-filled');
    }
  }

  /**
   * Progressive lock: nothing selected on open.
   *   Discipline (always open)
   *     → Session package  — unlocked once a discipline is chosen
   *       → Plan type (One time / Subscribe & Save) — unlocked once a
   *                           session package is chosen (its prices are
   *                           per-tier, so it waits on that choice now)
   *         → Athlete info  — unlocked once a session + plan resolve to a
   *                           real variant
   * Each locked step is dimmed (CSS via data-pc-locked) and its controls
   * disabled so it can't be interacted with out of order.
   */
  function applyGates(root) {
    var disciplineChosen = !!selectedDiscipline(root);
    // Athlete unlocks when the selection resolves to a real variant (the
    // chosen session's own variant, or its matching subscription variant).
    var variantChosen = !!resolveSelection(root).variantId;

    // Package (tabs + both panels' controls) unlocks on discipline alone —
    // there's no separate "plan" step anymore; price/plan is baked onto
    // each session card itself.
    setGate(root, 'package', !disciplineChosen);

    var tabButtons = root.querySelectorAll('[data-pc-purchase-tab]');
    for (var t = 0; t < tabButtons.length; t++) tabButtons[t].disabled = !disciplineChosen;

    var subCadenceRadios = root.querySelectorAll('[data-pc-sub-cadence]');
    for (var c = 0; c < subCadenceRadios.length; c++) subCadenceRadios[c].disabled = !disciplineChosen;

    var paygToggle = root.querySelector('[data-pc-payg-subscribe-toggle]');
    if (paygToggle) paygToggle.disabled = !disciplineChosen;

    var sessionRadios = root.querySelectorAll('[data-pc-session-option]');
    for (var i = 0; i < sessionRadios.length; i++) sessionRadios[i].disabled = !disciplineChosen;

    setGate(root, 'athlete', !variantChosen);
    var athlete = root.querySelector('[data-pc-gate="athlete"]');
    if (athlete) athlete.disabled = !variantChosen;
  }

  /** Toggle a step's locked (dimmed) state via data-pc-locked on its gate el. */
  function setGate(root, name, locked) {
    var el = root.querySelector('[data-pc-gate="' + name + '"]');
    if (el) {
      if (locked) el.setAttribute('data-pc-locked', '');
      else el.removeAttribute('data-pc-locked');
    }
  }

  /** Smooth-scroll a gated step into view, optionally after a delay (ms). */
  function scrollToGate(root, name, delay) {
    var el = root.querySelector('[data-pc-gate="' + name + '"]');
    if (!el) return;
    setTimeout(function () {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, delay || 0);
  }

  function setText(root, selector, text) {
    var el = root.querySelector(selector);
    if (el) el.textContent = text;
  }

  /**
   * Footer discipline label — the modal's shortDisciplineLabel: "Nutrition" /
   * "Mental Performance", not the full product title. Falls back to the title.
   */
  function shortDisciplineLabel(title) {
    var t = String(title || '');
    if (/nutrition/i.test(t)) return 'Nutrition';
    if (/mental/i.test(t)) return 'Mental Performance';
    return t;
  }

  /**
   * Lead API's img_marketodivision — its own valid values are "Mental
   * Performance" / "Nutrition Performance", NOT shortDisciplineLabel's
   * "Nutrition" (missing " Performance"), so this is a separate mapping
   * rather than reusing that one.
   */
  function leadMarketoDivision(title) {
    var t = String(title || '');
    if (/mental/i.test(t)) return 'Mental Performance';
    if (/nutrition/i.test(t)) return 'Nutrition Performance';
    return '';
  }

  /**
   * Discipline card descriptions are CSS-clamped to 2 lines by default (see
   * _personal-coach.scss &__discipline-descwrap). Wire each card's "Show
   * more" button to expand/collapse smoothly via a measured max-height
   * (line-clamp itself can't transition), and hide the button entirely on
   * a card whose description never actually overflows 2 lines. The card is
   * a <label> wrapping the radio — stopPropagation keeps the click from
   * bubbling up and selecting that discipline just from tapping the toggle.
   */
  function wireDisciplineDescToggles(root) {
    var toggles = root.querySelectorAll('[data-pc-desc-toggle]');
    for (var i = 0; i < toggles.length; i++) {
      (function (btn) {
        var wrap = btn.previousElementSibling;
        var desc = wrap && wrap.querySelector('[data-pc-desc]');
        if (!wrap || !desc) return;

        var collapsedHeight = wrap.getBoundingClientRect().height;
        if (desc.scrollHeight <= collapsedHeight + 1) {
          // Short description — nothing clamped, nothing to expand.
          btn.style.display = 'none';
          return;
        }

        btn.addEventListener('click', function (e) {
          e.preventDefault();
          e.stopPropagation();
          var isExpanded = btn.getAttribute('aria-expanded') === 'true';
          if (isExpanded) {
            wrap.style.maxHeight = collapsedHeight + 'px';
            desc.classList.remove('is-expanded');
            btn.textContent = 'Show more';
            btn.setAttribute('aria-expanded', 'false');
          } else {
            // Remove the clamp FIRST so scrollHeight measures the true full
            // height, not the still-clipped 2-line height.
            desc.classList.add('is-expanded');
            wrap.style.maxHeight = desc.scrollHeight + 'px';
            btn.textContent = 'Show less';
            btn.setAttribute('aria-expanded', 'true');
          }
        });
      })(toggles[i]);
    }
  }

  /**
   * Rebuild the session-package cards when the discipline changes — the
   * modal's discipline→variant re-derivation. Each discipline radio carries
   * its variants as JSON in data-pc-variants.
   */
  function wireDisciplineChange(root, onChange) {
    var radios = root.querySelectorAll('[data-pc-discipline]');
    wireToggleableRadios(radios);
    for (var i = 0; i < radios.length; i++) {
      radios[i].addEventListener('change', function () {
        // Read the LIVE selection rather than event.target — a toggle-off
        // click fires 'change' on the radio it just unchecked, so relying on
        // event.target would keep showing that radio's (now stale) variants.
        var disc = selectedDiscipline(root);
        // A stale "Save 10% extra" pick shouldn't survive a discipline
        // change — reset it BEFORE rebuilding so the rebuild below already
        // reflects standalone pricing (avoids a flash of subscribed pricing
        // under an unchecked toggle).
        resetPaygToggle(root);
        // Deselecting (toggle-off) must restore the shown-but-disabled
        // placeholder shell — the SAME one page.personal-coach.liquid
        // server-renders for the pre-pick frame — not an empty container.
        if (disc) {
          renderSessionOptions(root);
        } else {
          renderPlaceholderSessionOptions(root);
        }
        if (onChange) onChange();
      });
    }
  }

  /**
   * Radios normally can't be unchecked by clicking the already-selected one —
   * this repo's discipline / plan-type groups need that (deselecting resets
   * everything below it back to the page's default state).
   *
   * The radio itself is visually hidden (0x0, see input[type=radio] below), so
   * real taps/clicks land on the enclosing <label> card, not the input — a
   * listener on the input would never see a real pointerdown. We listen on
   * the label instead: 'pointerdown' there snapshots whether the radio was
   * ALREADY checked before this press (true prior state, unaffected by the
   * label's own click-forwarding timing); if the follow-up 'click' confirms
   * that, we preventDefault (stops the label from re-forwarding a check),
   * flip it off ourselves, and fire a synthetic 'change' so the existing
   * change-listeners (which do the actual downstream reset) run exactly as if
   * a different radio had been picked. Keyboard (Space/Enter) has no
   * pointerdown to snapshot, so it falls back to normal (non-toggle) radio
   * behavior rather than risking a stale flag — acceptable; most sites don't
   * support keyboard-deselect on radios either.
   */
  function wireToggleableRadios(radios) {
    for (var i = 0; i < radios.length; i++) {
      (function (radio) {
        var target = radio.closest('label') || radio;
        target.addEventListener('pointerdown', function () {
          radio.dataset.pcWasChecked = radio.checked ? '1' : '';
        });
        target.addEventListener('click', function (event) {
          if (radio.dataset.pcWasChecked === '1') {
            event.preventDefault();
            radio.dataset.pcWasChecked = '';
            radio.checked = false;
            radio.dispatchEvent(new Event('change', { bubbles: true }));
          } else {
            radio.dataset.pcWasChecked = '';
          }
        });
      })(radios[i]);
    }
  }

  function parseVariants(raw) {
    if (!raw) return [];
    try {
      var parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }

  /** Leading session count off a variant title ("12 Sessions" -> 12); NaN if none. */
  function sessionCountFromLabel(label) {
    var m = /^\s*(\d+)/.exec(label || '');
    return m ? parseInt(m[1], 10) : NaN;
  }

  /**
   * Variant titles are merchandiser-entered and sometimes already carry a
   * hand-typed price ("12 Sessions – $1,099") — strip that back off so we
   * never double up when we append our OWN computed price/savings text.
   * Em dash matches resolveSelection's packageLabel stripping (line ~398);
   * en dash/hyphen covered too since staff haven't been consistent.
   */
  function cleanSessionLabel(label) {
    return (label || '').replace(/\s*[-–—]\s*\$.*$/, '').trim();
  }

  /**
   * Build one session-package card — same markup/classes used throughout
   * (personal-coach__plan-option). mode decides which variant/price gets
   * baked onto the radio (data-pc-price/-selling-plan), NOT a later lookup:
   *   • 'standalone' — the STANDALONE variant's own (one-time) price, no plan.
   *   • 'subscription' — that SAME tier's subscription variant + matching
   *     selling plan (findSubscriptionForTier) — each tier has its own.
   * tabName becomes the radio group's name ("pc-session-subscription" /
   * "pc-session-payg") so the two panels' radios never collide.
   * SESSION_LABEL_OVERRIDE swaps 2/4 sessions' title for Off-season/In-season.
   */
  function buildSessionOptionCard(root, v, mode, tabName) {
    var n = sessionCountFromLabel(v.label);
    var label = cleanSessionLabel(v.label);
    var override = SESSION_LABEL_OVERRIDE[n];

    var card = document.createElement('label');
    card.className = 'personal-coach__plan-option';

    var badgeText = SHOW_SESSION_BADGES ? SESSION_BADGE[n] : null;
    if (badgeText) {
      var badge = document.createElement('span');
      badge.className = 'personal-coach__card-badge';
      badge.textContent = badgeText;
      card.appendChild(badge);
    }

    var input = document.createElement('input');
    input.type = 'radio';
    input.name = 'pc-session-' + tabName;
    input.setAttribute('data-pc-session-option', '');
    // Pay yearly bills ONE lump sum for the whole year's worth of this
    // tier's monthly allotment (n * 12 sessions total) — the footer's
    // "Coaching package" field should say THAT total, not the per-month
    // count ("4 Sessions"), which only applies while billed monthly.
    var packageLabel = (override && mode === 'standalone') ? ((n * 12) + ' Sessions') : label;
    input.setAttribute('data-pc-label', packageLabel);
    if (n > 0) input.setAttribute('data-pc-session-count', n);
    card.appendChild(input);

    var text = document.createElement('span');
    text.className = 'personal-coach__plan-text';

    // Resolve variant/price/plan for the requested mode — baked onto the
    // radio now, read verbatim later by resolveSelection.
    var priceCents = null;
    var variantId = v.id;
    var sellingPlanId = null;
    if (mode === 'subscription') {
      var sub = findSubscriptionForTier(root, n);
      if (sub) {
        priceCents = sub.priceCents;
        variantId = sub.variantId;
        sellingPlanId = sub.sellingPlanId;
      }
    } else if (tabName === 'subscription') {
      // Pay yearly — the dedicated "N Sessions Billed Yearly" subscription
      // variant. `v` here is the plain monthly-cadence variant (title/id
      // default only); if the Billed Yearly variant/plan isn't there yet,
      // falls back to '—' rather than a wrong price.
      var yearly = findYearlyVariantForTier(root, n);
      if (yearly) {
        priceCents = yearly.priceCents;
        variantId = yearly.variantId;
        sellingPlanId = yearly.sellingPlanId;
      }
    } else {
      priceCents = v.price;
    }
    input.value = variantId;
    if (priceCents != null) input.setAttribute('data-pc-price', priceCents);
    if (sellingPlanId) input.setAttribute('data-pc-selling-plan', sellingPlanId);

    // Validity window for the roll-over/"valid through" disclaimer — NOT the
    // same thing as SESSION_TO_CADENCE_MONTHS for the override (2/4-session)
    // tiers specifically: those now bill either monthly OR yearly depending
    // on which cadence is active, so their window must follow the actual
    // chosen cadence (1 vs 12 months), not the tier's fixed table value.
    // Pay as you go's 6/12/18/24 tiers are unaffected — their validity window
    // is the session-pack length itself, the same regardless of standalone
    // vs. Subscribe & Save.
    if (n > 0) {
      var validMonths = override ? (mode === 'standalone' ? 12 : 1) : SESSION_TO_CADENCE_MONTHS[n];
      input.setAttribute('data-pc-valid-months', validMonths);
    }

    if (override) {
      // Subscription tab's 2/4-session cards (title is ALWAYS truthy here —
      // tabName is 'subscription' iff n is 2 or 4): title / "N sessions a
      // month" / big per-month rate / billed-rate line. Pay yearly's
      // headline number is the MONTHLY-EQUIVALENT rate (annual ÷ 12), with
      // the real annual billing math (full price crossed out vs. the
      // discounted one) spelled out underneath — not the annual lump sum
      // itself, which would read as a much bigger, scarier number up top.
      var title = document.createElement('span');
      title.className = 'personal-coach__plan-title';
      title.textContent = override;
      text.appendChild(title);

      var subhead = document.createElement('span');
      subhead.className = 'personal-coach__plan-subhead';
      subhead.textContent = n + ' sessions a month';
      text.appendChild(subhead);

      if (n > 0 && priceCents != null) {
        // Pay yearly's priceCents is the ANNUAL lump sum — its per-session
        // rate divides by the full year's sessions (12 * n), same convention
        // as the "Save X%" math, landing at half the monthly per-session
        // rate when the catalog's yearly discount is 50%.
        var sessionsForRate = (mode === 'standalone') ? n * 12 : n;
        var perSessionDollars = Math.round(priceCents / sessionsForRate / 100);
        var subEyebrow = document.createElement('span');
        subEyebrow.className = 'personal-coach__plan-eyebrow';
        subEyebrow.textContent = formatPrice(perSessionDollars * 100) + ' / session';
        text.appendChild(subEyebrow);
      }

      if (priceCents != null) {
        // Pay yearly's annual lump sum rarely divides into a whole-dollar
        // monthly rate — round at the DOLLAR level (not the cent), same
        // convention as the per-session rate elsewhere in this file.
        var perMonthCents = (mode === 'standalone') ? Math.round(priceCents / 12 / 100) * 100 : priceCents;
        var priceLg = document.createElement('span');
        priceLg.className = 'personal-coach__plan-price-lg';
        priceLg.textContent = formatPrice(perMonthCents);
        var suffix = document.createElement('span');
        suffix.className = 'personal-coach__plan-price-suffix';
        suffix.textContent = '/mo';
        priceLg.appendChild(suffix);
        text.appendChild(priceLg);
      }

      var billed = document.createElement('span');
      billed.className = 'personal-coach__plan-billed';
      if (mode === 'standalone') {
        // The full (non-discounted) cost is this SAME tier's real monthly
        // rate × 12 — fetched regardless of which cadence is active, so
        // Pay yearly can show what the shopper is actually saving against.
        var monthlySub = findSubscriptionForTier(root, n);
        if (monthlySub) {
          var strike = document.createElement('span');
          strike.className = 'personal-coach__plan-strike';
          strike.textContent = formatPrice(monthlySub.priceCents * 12);
          billed.appendChild(strike);
          billed.appendChild(document.createTextNode(' '));
        }
        var yearlyAmount = document.createElement('span');
        yearlyAmount.className = 'personal-coach__plan-billed-amount';
        yearlyAmount.textContent = priceCents != null ? formatPrice(priceCents) : '—';
        billed.appendChild(yearlyAmount);
        billed.appendChild(document.createTextNode(' billed yearly'));
        input.setAttribute('data-pc-billed-text', 'billed yearly');
      } else {
        billed.appendChild(document.createTextNode('billed monthly'));
        input.setAttribute('data-pc-billed-text', 'billed monthly');
      }
      text.appendChild(billed);
    } else {
      // Pay as you go cards — title / per-session eyebrow / price / billed.
      var paygTitle = document.createElement('span');
      paygTitle.className = 'personal-coach__plan-title';
      paygTitle.textContent = label;
      text.appendChild(paygTitle);

      if (n > 0 && priceCents != null) {
        // priceCents is CENTS; round at the DOLLAR level (not cents) per the
        // "whole dollars" ask, then hand formatPrice a clean whole-dollar
        // cents value (×100) so it still gets the $ + thousands-separator
        // formatting for free instead of reimplementing it here.
        var perSessionDollars = Math.round(priceCents / n / 100);
        var eyebrow = document.createElement('span');
        eyebrow.className = 'personal-coach__plan-eyebrow';
        eyebrow.textContent = formatPrice(perSessionDollars * 100) + ' / session';
        text.appendChild(eyebrow);
      }

      // Same big/bold price treatment as the Subscription tab's cards —
      // no "/mo" suffix here (payg's price is the pack total, not a
      // per-month rate), just the shared &__plan-price-lg typography.
      var price = document.createElement('span');
      price.className = 'personal-coach__plan-price-lg';
      price.textContent = priceCents != null ? formatPrice(priceCents) : '—';
      text.appendChild(price);

      if (n > 0) {
        var billedText = mode === 'subscription'
          ? ('billed every ' + pluralMonths(SESSION_TO_CADENCE_MONTHS[n]).toLowerCase())
          : 'one time';
        var billing = document.createElement('span');
        billing.className = 'personal-coach__plan-billed';
        billing.textContent = billedText;
        text.appendChild(billing);
        input.setAttribute('data-pc-billed-text', billedText);
      }
    }

    card.appendChild(text);

    var radio = document.createElement('span');
    radio.className = 'personal-coach__radio';
    radio.setAttribute('aria-hidden', 'true');
    radio.innerHTML = '<span class="personal-coach__radio-dot"></span>';
    card.appendChild(radio);

    return card;
  }

  /**
   * Rebuild ONE tab panel's session cards for the chosen discipline's
   * variants, filtered to that panel's own fixed tier set. Preserves
   * whichever tier was already checked (by session count) across a
   * cadence/toggle-driven price swap, so re-checking the matching card keeps
   * the shopper's pick instead of silently clearing it.
   *
   * The Subscription tab's 2/4-session tiers live ENTIRELY on the
   * subscription product now (no standalone 2/4-session variant exists at
   * all — Pay monthly and Pay yearly are both subscription-product variants,
   * just with a different cadence/plan) — buildSessionOptionCard/
   * findSubscriptionForTier/findYearlyVariantForTier resolve the actual
   * price from there regardless; the plain (non-"Billed Yearly") variant
   * passed in here only supplies the card's base id/label/title.
   */
  function renderSessionOptionsForPanel(root, tabName, tierCounts, mode) {
    var container = root.querySelector('[data-pc-session-options="' + tabName + '"]');
    if (!container) return;

    var disc = selectedDiscipline(root);
    var variants = disc
      ? parseVariants(disc.getAttribute(tabName === 'subscription' ? 'data-pc-sub-variants' : 'data-pc-variants'))
      : [];
    var filtered = variants.filter(function (v) {
      return tierCounts.indexOf(sessionCountFromLabel(v.label)) !== -1 && !isBilledYearlyLabel(v.label);
    });

    var prevChecked = container.querySelector('[data-pc-session-option]:checked');
    var prevCount = prevChecked ? parseInt(prevChecked.getAttribute('data-pc-session-count'), 10) : null;

    container.innerHTML = '';
    filtered.forEach(function (v) {
      container.appendChild(buildSessionOptionCard(root, v, mode, tabName));
    });

    if (prevCount) {
      var toRecheck = container.querySelector('[data-pc-session-count="' + prevCount + '"]');
      if (toRecheck) toRecheck.checked = true;
    }

    wireToggleableRadios(container.querySelectorAll('[data-pc-session-option]'));
  }

  /** Rebuild BOTH tab panels' session cards — called on discipline change. */
  function renderSessionOptions(root) {
    renderSessionOptionsForPanel(root, 'subscription', SUBSCRIPTION_TAB_TIERS, subCadenceMode(root));
    renderSessionOptionsForPanel(root, 'payg', PAYG_TAB_TIERS, paygToggleMode(root));
  }

  /**
   * Restore the shown-but-disabled placeholder shell in BOTH panels (no
   * discipline chosen) — the same price-less, disabled cards
   * page.personal-coach.liquid server-renders for the pre-pick frame. Needed
   * because deselecting a discipline (wireToggleableRadios' toggle-off)
   * otherwise leaves renderSessionOptions([]) having wiped the containers to
   * nothing instead of back to that placeholder.
   */
  function renderPlaceholderSessionOptions(root) {
    renderPlaceholderPanel(root, 'subscription', SUBSCRIPTION_TAB_TIERS);
    renderPlaceholderPanel(root, 'payg', PAYG_TAB_TIERS);
  }

  function renderPlaceholderPanel(root, tabName, tierCounts) {
    var container = root.querySelector('[data-pc-session-options="' + tabName + '"]');
    if (!container) return;

    container.innerHTML = '';
    tierCounts.forEach(function (n) {
      var card = document.createElement('label');
      card.className = 'personal-coach__plan-option';

      var badgeText = SHOW_SESSION_BADGES ? SESSION_BADGE[n] : null;
      if (badgeText) {
        var badge = document.createElement('span');
        badge.className = 'personal-coach__card-badge';
        badge.textContent = badgeText;
        card.appendChild(badge);
      }

      var input = document.createElement('input');
      input.type = 'radio';
      input.name = 'pc-session-' + tabName;
      input.setAttribute('data-pc-session-option', '');
      input.disabled = true;
      card.appendChild(input);

      var text = document.createElement('span');
      text.className = 'personal-coach__plan-text';
      var title = document.createElement('span');
      title.className = 'personal-coach__plan-title';
      var override = SESSION_LABEL_OVERRIDE[n];
      if (override) {
        title.textContent = override;
        text.appendChild(title);
        var subhead = document.createElement('span');
        subhead.className = 'personal-coach__plan-subhead';
        subhead.textContent = n + ' Sessions';
        text.appendChild(subhead);
      } else {
        title.textContent = n + ' Sessions';
        text.appendChild(title);
      }
      var price = document.createElement('span');
      price.className = 'personal-coach__plan-price-lg';
      price.textContent = '—';
      text.appendChild(price);
      card.appendChild(text);

      var radio = document.createElement('span');
      radio.className = 'personal-coach__radio';
      radio.setAttribute('aria-hidden', 'true');
      radio.innerHTML = '<span class="personal-coach__radio-dot"></span>';
      card.appendChild(radio);

      container.appendChild(card);
    });
  }

  /** Zero-pad a number to 2 digits ("3" -> "03"). */
  function pad2(n) {
    return (n < 10 ? '0' : '') + n;
  }

  /**
   * Today's date plus N months, as "MM/DD/YYYY" — the session package's
   * expiration, per SESSION_TO_CADENCE_MONTHS (6 sessions = 3 months, etc.).
   * Date#setMonth normalizes an out-of-range day by rolling into the next
   * month (e.g. Jan 31 + 1 month -> Mar 3, not Feb 31) — an edge case, not a
   * bug worth working around for a display-only disclaimer date.
   */
  function sessionValidThroughDate(months) {
    var d = new Date();
    d.setMonth(d.getMonth() + months);
    return pad2(d.getMonth() + 1) + '/' + pad2(d.getDate()) + '/' + d.getFullYear();
  }

  /** Shopify variant.price is in CENTS; format to "$799". */
  function formatPrice(cents) {
    var dollars = (parseInt(cents, 10) || 0) / 100;
    var str = dollars % 1 === 0 ? String(dollars) : dollars.toFixed(2);
    // Thousands separator on the integer part only (same pattern as cart.js's
    // price formatting) — "$2799" -> "$2,799", "$1499.50" -> "$1,499.50".
    var parts = str.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return '$' + parts.join('.');
  }

  function readAthlete(root) {
    function val(name) {
      var el = root.querySelector('[name="' + name + '"]');
      return el ? el.value.trim() : '';
    }
    return {
      firstName: val('firstName'),
      lastName: val('lastName'),
      email: val('email'),
      phone: val('phoneNumber'),
      guardianFirstName: val('guardianFirstName'),
      guardianLastName: val('guardianLastName'),
      guardianEmail: val('guardianEmail'),
      guardianPhone: val('guardianPhone')
    };
  }

  // ──────────────────────────────────────────────────────────────────────────
  // PUBLIC API
  // ──────────────────────────────────────────────────────────────────────────

  return {
    init: init,
    // exported for tests
    getParam: getParam,
    getCookie: getCookie,
    resolveIdentity: resolveIdentity,
    normalizeShareWith: normalizeShareWith,
    buildAddToCartUrl: buildAddToCartUrl,
    formatPrice: formatPrice,
    isValidEmail: isValidEmail,
    shortDisciplineLabel: shortDisciplineLabel,
    _config: CONFIG
  };
});
